import { Injectable } from '@angular/core';
import { FlowDesign } from 'src/app/features/chat-bot/types/chat-flow.interface';

@Injectable({
  providedIn: 'root'
})
export class BotStylingService {
  applyStyles(
    selector: string,
    styles: Record<string, string>,
    shadowRoot: ShadowRoot | null = null,
    useImportant: boolean = true
  ): void {
    const styleContent = this.generateStyleContent(
      selector,
      styles,
      useImportant
    );
    this.appendStyleToHead(styleContent, shadowRoot);
  }

  private generateStyleContent(
    selector: string,
    styles: Record<string, string>,
    useImportant: boolean = true
  ): string {
    const noImportantProperties = [
      'background-image',
      'background-size',
      'background-position'
    ];
    let styleContent = `${selector} { `;
    Object.keys(styles).forEach((styleName) => {
      if (!useImportant || noImportantProperties.includes(styleName)) {
        styleContent += `${styleName}: ${styles[styleName]}; `;
      } else {
        styleContent += `${styleName}: ${styles[styleName]} !important; `;
      }
    });
    styleContent += `}`;
    return styleContent;
  }

  private appendStyleToHead(
    styleContent: string,
    shadowRoot: ShadowRoot | null
  ): void {
    const styleTag = document.createElement('style');
    styleTag.type = 'text/css';
    styleTag.innerHTML = styleContent;

    if (shadowRoot) {
      shadowRoot.appendChild(styleTag);
    } else {
      document.getElementsByTagName('head')[0].appendChild(styleTag);
    }
  }

  applyDynamicStyling(
    flowDesign: FlowDesign,
    shadowRoot: ShadowRoot | null = null
  ): void {
    if (flowDesign) {
      this.applyStyles('html', {
        'font-family': `${flowDesign.typography.custom_font_url ? 'CustomChatBotFont' : flowDesign.typography.font_family}, Montserrat, Arial, sans-serif`,
        '--mdc-typography-font-family': `${flowDesign.typography.custom_font_url ? 'CustomChatBotFont' : flowDesign.typography.font_family}, Montserrat, Arial, sans-serif`,
        '--mdc-typography-button-font-family': `${flowDesign.typography.custom_font_url ? 'CustomChatBotFont' : flowDesign.typography.font_family}, Montserrat, Arial, sans-serif`,
        '--mat-menu-item-label-text-font': `${flowDesign.typography.custom_font_url ? 'CustomChatBotFont' : flowDesign.typography.font_family}, Montserrat, Arial, sans-serif`,
        //these for mat-flat-button
        '--mdc-text-button-label-text-font': `${flowDesign.typography.custom_font_url ? 'CustomChatBotFont' : flowDesign.typography.font_family}, Montserrat, Arial, sans-serif`,
        '--mdc-filled-button-label-text-font': `${flowDesign.typography.custom_font_url ? 'CustomChatBotFont' : flowDesign.typography.font_family}, Montserrat, Arial, sans-serif`,
        '--mdc-outlined-button-label-text-font': `${flowDesign.typography.custom_font_url ? 'CustomChatBotFont' : flowDesign.typography.font_family}, Montserrat, Arial, sans-serif`,
        '--mdc-filled-button-label-text-weight': '400',
        '--mat-sys-label-large-font': `${flowDesign.typography.custom_font_url ? 'CustomChatBotFont' : flowDesign.typography.font_family}, Montserrat, Arial, sans-serif`,
        '--mat-sys-label-large-size': '16px',
        '--mat-sys-label-large-weight': '400',
        '--mat-sys-label-large-tracking': 'normal'
      });

      const backgroundStyles: { [key: string]: string } = {};

      if (flowDesign.background) {
        if (flowDesign.background.background_type === 'image') {
          const desktopImage = flowDesign.background.images?.desktop;
          const mobileImage = flowDesign.background.images?.mobile;

          if (desktopImage) {
            backgroundStyles['background-image'] = `url(${desktopImage})`;
            backgroundStyles['background-size'] = 'cover'; // Optional: Ensure the image covers the entire background
            backgroundStyles['background-position'] = 'bottom center'; // Optional: Ensure the image is centered
          }

          if (mobileImage) {
            const styleElement = document.createElement('style');
            styleElement.textContent = `
              @media (max-width: 768px) {
                .app-container-chatbot {
                  background-image: url(${mobileImage}) !important;
                  background-size: cover !important;
                  background-position: bottom center !important;
                }
              }
            `;
            if (shadowRoot) {
              shadowRoot.appendChild(styleElement);
            } else {
              document.head.appendChild(styleElement);
            }
          }
        } else if (
          flowDesign.background.background_type === 'solid_color' &&
          flowDesign.background.background_color
        ) {
          backgroundStyles['background-color'] =
            flowDesign.background.background_color;
          backgroundStyles['background-image'] = 'none';
        } else if (
          flowDesign.background.background_type === 'gradient' &&
          flowDesign.background.gradient_background
        ) {
          backgroundStyles['background-image'] =
            flowDesign.background.gradient_background.background_gradient_css;
          backgroundStyles['background-color'] = 'transparent';
        }

        this.applyStyles(
          '.app-container-chatbot',
          backgroundStyles,
          shadowRoot
        );
      }
      // end
      this.applyStyles(
        '.text-help',
        {
          color: flowDesign.help_text_color
        },
        shadowRoot,
        false
      );
      this.applyStyles(
        '.mdc-checkbox',
        {
          color: flowDesign.typography.bot_text_color
        },
        shadowRoot
      );
      this.applyStyles(
        'button.user-option-button',
        {
          'border-radius': flowDesign.button.border_radius + 'px'
        },
        shadowRoot,
        false
      );
      this.applyStyles(
        '.agent-message-box',
        {
          color: flowDesign.typography.bot_text_color
        },
        shadowRoot
      );
      this.applyStyles(
        '.feedback-checkbox-option label',
        {
          color: flowDesign.typography.bot_text_color
        },
        shadowRoot
      );
      this.applyStyles(
        '.user-message-box',
        {
          color: flowDesign.button.text_color,
          'background-color': flowDesign.button.background_color
        },
        shadowRoot,
        false
      );

      this.applyStyles(
        '.mat-mdc-unelevated-button',
        {
          '--mdc-filled-button-container-color':
            flowDesign.button.background_color,
          '--mdc-filled-button-label-text-color': flowDesign.button.text_color
        },
        shadowRoot
      );

      this.applyStyles(
        '.user-option-button.mat-mdc-unelevated-button.outline:not(:disabled)',
        {
          color: flowDesign.button.background_color,
          'background-color': flowDesign.button.text_color,
          border: `1px solid ${flowDesign.button.background_color}`
        },
        shadowRoot
      );

      this.applyStyles('html', {
        '--mat-autocomplete-background-color': '#fff',
        '--mat-select-panel-background-color': '#fff',
        '--mdc-outlined-text-field-focus-label-text-color':
          flowDesign.button.background_color,
        '--mdc-filled-text-field-focus-label-text-color':
          flowDesign.button.background_color,
        '--mdc-outlined-text-field-input-text-color':
          flowDesign.typography.bot_text_color,
        '--mdc-outlined-text-field-input-text-placeholder-color':
          'rgba(0, 0, 0, 0.38)',
        '--mat-option-text-color': flowDesign.typography.bot_text_color
      });
      this.applyStyles('.mat-mdc-option:hover:not(.mdc-list-item--disabled)', {
        'background-color': '#f0f0f0'
      });
      this.applyStyles(
        '.mat-mdc-option.mat-mdc-option-active:not(.mdc-list-item--disabled)',
        {
          'background-color': '#f0f0f0'
        }
      );
      const botTextColorWithOpacity =
        flowDesign.typography.bot_text_color + '33';
      this.applyStyles(
        ':host',
        {
          '--dot-color': flowDesign.typography.bot_text_color,
          '--dot-color-20': botTextColorWithOpacity
        },
        shadowRoot
      );
    }
  }
}
